import { Fragment, ReactNode, memo, useState } from "react";
import { useField } from "formik";
import classNames from "classnames";
import {
	FloatingPortal,
	Placement,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useTransitionStyles,
} from "@floating-ui/react";
import { isEmpty } from "lodash";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import {
	DestinationCombobox,
	DestinationComboboxProps,
} from "app/features/SearchEngine/searchJourney/components/locationCombobox/DestinationCombobox";
import { SearchOption } from "app/features/SearchEngine/searchJourney/types";
import "app/features/SearchEngine/searchJourney/components/locationCombobox/LocationCombobox.scss";
import "./FloatingAdvancedSelectFormik.scss";
import { FormattedMessage } from "react-intl";

type FloatingDestinationComboboxProps = {
	name: string;
	id: string;
	label: ReactNode;
	className?: string;
	isRequired?: boolean;
	floatingConfig?: {
		placement?: Placement;
		offset?: Parameters<typeof offset>[0];
	};
	usePortalForMenu?: boolean;
	selectRef?: React.MutableRefObject<HTMLDivElement | null>;
	getOptionLabel?: (option: SearchOption) => string;
	loading?: boolean;
	"data-testid"?: string;
	onChange?: DestinationComboboxProps["onChange"];
	renderDestinationOption?: (option: SearchOption) => ReactNode;
	onFocus?: DestinationComboboxProps["onFocus"];
} & Pick<
	DestinationComboboxProps,
	"options" | "optionsWhenEmpty" | "placeholder" | "hotelSearchPath"
>;

const FloatingDestinationCombobox = ({
	id,
	name,
	label,
	className,
	isRequired,
	floatingConfig = {},
	usePortalForMenu,
	selectRef,
	getOptionLabel = () => "",
	renderDestinationOption = () => null,
	loading,
	onChange,
	"data-testid": dataTestId,
	...rest
}: FloatingDestinationComboboxProps) => {
	const [field, meta] = useField(name);
	const [showFloatingModal, setFloatingModal] = useState(false);

	const isTouched = (meta.touched && !meta.error) || Boolean(field.value);

	const { x, y, refs, strategy, context } = useFloating({
		placement: floatingConfig.placement,
		strategy: "absolute",
		open: showFloatingModal,
		onOpenChange: setFloatingModal,
		middleware: [offset(floatingConfig.offset), shift()],
	});

	const dismiss = useDismiss(context, { ancestorScroll: false });
	const click = useClick(context, {
		event: "mousedown",
		toggle: false,
		keyboardHandlers: false,
	});
	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
	const { styles: transitionStyles, isMounted } = useTransitionStyles(context, {
		duration: 250,
		initial: { opacity: 0, transform: "scale(0.95)" },
		open: { opacity: 1, transform: "scale(1)" },
		close: { opacity: 0, transform: "scale(0.95)" },
	});

	const containerClassName = classNames(
		"floating-advanced-select-container",
		"floating-destination-combobox",
		className,
		{
			"floating-advanced-select-container--required": isRequired,
			"floating-advanced-select-container--touched": isTouched,
			"floating-advanced-select-container--error": meta.touched && meta.error,
			"floating-advanced-select-container--focus": showFloatingModal,
		}
	);

	const PortalOrFragment = usePortalForMenu ? FloatingPortal : Fragment;

	return (
		<div
			className={containerClassName}
			ref={refs.setReference}
			{...getReferenceProps({})}
			data-testid={dataTestId}
		>
			{!loading ? (
				<div className="floating-advanced-select__label-container" ref={selectRef}>
					<label htmlFor={id} className="floating-advanced-select__label">
						<Typography
							variant={
								field.value
									? TYPOGRAPHY_VARIANTS.XSMALL
									: TYPOGRAPHY_VARIANTS.REGULAR
							}
							data-testid={`${dataTestId}-label`}
						>
							{label}
						</Typography>
						{field.value && (
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								className="floating-advanced-select__value"
							>
								{getOptionLabel(field.value as SearchOption)}
							</Typography>
						)}
					</label>
				</div>
			) : (
				<div className="floating-advanced-select__loader">
					<LoaderBar height={10} width="80%" />
					<LoaderBar height={10} />
				</div>
			)}

			{isMounted && (
				<PortalOrFragment>
					<div
						className={classNames(
							"floating-advanced-select__modal",
							"floating-advanced-select__modal--destination",
							{
								"floating-advanced-select__modal--touched": isTouched,
							}
						)}
						data-testid={dataTestId ? `${dataTestId}-floating` : undefined}
						ref={refs.setFloating}
						{...getFloatingProps({
							style: {
								position: strategy,
								left: x,
								top: y,
								zIndex: 10,
								...transitionStyles,
							},
						})}
					>
						<DestinationCombobox
							id={id}
							autoFocus
							label={label}
							value={(field.value as SearchOption) || null}
							noOptionsMessage={() => {
								return <FormattedMessage id="sdp.search.no.result.label" />;
							}}
							placeholder={
								<FormattedMessage id="sdp.search.destination.input.label.mobile" />
							}
							renderDestinationOption={renderDestinationOption}
							menuListMaxHeight={485}
							classNamePrefix="advanced-select__select-sdp"
							onChange={(option, meta) => {
								if (onChange) {
									onChange(option, meta);
								}
								if (option && !isEmpty(option)) {
									setFloatingModal(false);
								}
							}}
							{...rest}
						/>
					</div>
				</PortalOrFragment>
			)}
		</div>
	);
};

export default memo(FloatingDestinationCombobox);
